import { Bar, CartesianGrid, ComposedChart, Line, XAxis, YAxis } from 'recharts';
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/components/ui/chart';
import { PLATFORM_COLOR, PLATFORM_LABEL } from '../../../lib/reporting/socialExplorer';
import type { CadenceWeek } from '../../../lib/reporting/socialContent';
import type { SocialPlatform } from '../../../types/socialReporting';

// Content tab — Posting cadence: posts per week (stacked by platform, from the
// mirror ∪ external union — covers FB/IG/GBP) with the FB/IG engagement overlay on
// a secondary axis, so "did volume match results?" reads at a glance.

const ENGAGEMENT_COLOR = '#0f766e'; // kyck teal-ish, distinct from the platform bars

function labelWeek(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', timeZone: 'UTC' });
}

export function CadenceSection({
  cadence,
  platforms,
}: {
  cadence: CadenceWeek[];
  platforms: SocialPlatform[];
}) {
  const config: ChartConfig = {
    engagement: { label: 'Engagement', color: ENGAGEMENT_COLOR },
    ...Object.fromEntries(platforms.map((p) => [p, { label: PLATFORM_LABEL[p], color: PLATFORM_COLOR[p] }])),
  };

  if (cadence.length === 0) {
    return <div className="flex h-56 items-center justify-center text-sm text-slate-400">No posts in this period.</div>;
  }

  return (
    <ChartContainer config={config} className="aspect-auto h-64 w-full">
      <ComposedChart data={cadence} margin={{ left: 4, right: 8, top: 4, bottom: 0 }} accessibilityLayer>
        <CartesianGrid vertical={false} strokeDasharray="3 3" />
        <XAxis dataKey="weekStart" tickLine={false} axisLine={false} tickMargin={8} minTickGap={16} tickFormatter={labelWeek} />
        <YAxis yAxisId="left" allowDecimals={false} width={24} tickLine={false} axisLine={false} />
        <YAxis yAxisId="right" orientation="right" width={28} tickLine={false} axisLine={false} />
        <ChartTooltip
          content={
            <ChartTooltipContent
              labelFormatter={(v) => `Week of ${labelWeek(String(v))}`}
              formatter={(value, name) => (
                <span className="flex w-full items-center justify-between gap-4">
                  <span className="capitalize text-muted-foreground">{name}</span>
                  <span className="font-mono font-medium tabular-nums text-foreground">{Number(value).toLocaleString()}</span>
                </span>
              )}
            />
          }
        />
        {platforms.map((p) => (
          <Bar
            key={p}
            yAxisId="left"
            dataKey={p}
            name={PLATFORM_LABEL[p]}
            stackId="volume"
            fill={PLATFORM_COLOR[p]}
            maxBarSize={40}
            isAnimationActive={false}
          />
        ))}
        <Line
          yAxisId="right"
          type="monotone"
          dataKey="engagement"
          name="Engagement"
          stroke={ENGAGEMENT_COLOR}
          strokeWidth={2}
          dot={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ChartContainer>
  );
}
